import {
  Badge,
  Box,
  Code,
  Flex,
  Grid,
  Heading,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  FiChevronDown,
  FiChevronRight,
  FiCopy,
  FiLock,
  FiPlay,
  FiZap,
} from "react-icons/fi"

import { IconBox, VisionButton, VisionCard } from "@/components/VisionUI"
import { useColorModeValue } from "@/components/ui/color-mode"
import { useSemanticColors } from "@/hooks/useSemanticColors"

export const Route = createFileRoute("/_layout/api-explorer")({
  component: ApiExplorer,
})

// ─── Types ───────────────────────────────────────────────────────────────────

interface OApiParam {
  name: string
  in: "path" | "query" | "header" | "cookie"
  required?: boolean
  description?: string
  schema?: {
    type?: string
    default?: string | number | boolean
    enum?: string[]
  }
}

interface OApiOperation {
  operationId?: string
  summary?: string
  description?: string
  tags?: string[]
  parameters?: OApiParam[]
  requestBody?: {
    required?: boolean
    content?: Record<string, { schema?: object }>
    description?: string
  }
  responses?: Record<string, { description?: string }>
}

interface OApiSpec {
  info?: { title?: string; version?: string; description?: string }
  paths: Record<string, Record<string, OApiOperation>>
  tags?: Array<{ name: string; description?: string }>
}

interface EndpointDef {
  method: string
  path: string
  fullPath: string
  summary: string
  description?: string
  tag: string
  pathParams: OApiParam[]
  queryParams: OApiParam[]
  hasBody: boolean
  bodyHint?: string
}

interface ResponseState {
  status: number
  statusText: string
  body: string
  ms: number
  headers: Record<string, string>
}

// ─── Constants ───────────────────────────────────────────────────────────────

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? ""

const METHOD_COLOR: Record<string, string> = {
  GET: "#38A169",
  POST: "#3182CE",
  PUT: "#DD6B20",
  PATCH: "#805AD5",
  DELETE: "#E53E3E",
}

const METHOD_BG: Record<string, string> = {
  GET: "rgba(56,161,105,0.12)",
  POST: "rgba(49,130,206,0.12)",
  PUT: "rgba(221,107,32,0.12)",
  PATCH: "rgba(128,90,213,0.12)",
  DELETE: "rgba(229,62,62,0.12)",
}

// Quick-demo presets — pre-filled requests for common ERP use cases.
// `labelKey` is a translation key under the `apiExplorer.demos` namespace.
const QUICK_DEMOS = [
  {
    labelKey: "demos.healthCheck",
    icon: "🏥",
    method: "GET",
    path: "/api/v1/utils/health-check/",
    pathParams: {} as Record<string, string>,
    queryParams: {} as Record<string, string>,
    body: "",
  },
  {
    labelKey: "demos.temperatureHistory",
    icon: "🌡️",
    method: "GET",
    path: "/api/v1/tags/{id}/readings",
    pathParams: { id: "" },
    queryParams: { limit: "100", skip: "0" },
    body: "",
    highlight: true,
  },
  {
    labelKey: "demos.listAllTags",
    icon: "📡",
    method: "GET",
    path: "/api/v1/tags/",
    pathParams: {},
    queryParams: { skip: "0", limit: "20" },
    body: "",
  },
  {
    labelKey: "demos.listReaders",
    icon: "📶",
    method: "GET",
    path: "/api/v1/readers/",
    pathParams: {},
    queryParams: { skip: "0", limit: "20" },
    body: "",
  },
  {
    labelKey: "demos.tagEvents",
    icon: "📋",
    method: "GET",
    path: "/api/v1/tags/{id}/events",
    pathParams: { id: "" },
    queryParams: { limit: "50" },
    body: "",
  },
  {
    labelKey: "demos.currentUser",
    icon: "👤",
    method: "GET",
    path: "/api/v1/users/me",
    pathParams: {},
    queryParams: {},
    body: "",
  },
] as const

// ─── Utilities ────────────────────────────────────────────────────────────────

function getToken(): string | null {
  return localStorage.getItem("access_token")
}

function formatJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2)
  } catch {
    return raw
  }
}

function extractPathParams(path: string): string[] {
  return [...path.matchAll(/\{([^}]+)\}/g)].map((m) => m[1])
}

function buildUrl(
  path: string,
  pathParams: Record<string, string>,
  queryParams: Record<string, string>,
): string {
  let url = `${API_BASE}${path}`
  for (const [k, v] of Object.entries(pathParams)) {
    url = url.replace(`{${k}}`, encodeURIComponent(v))
  }
  const qs = Object.entries(queryParams)
    .filter(([, v]) => v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&")
  return qs ? `${url}?${qs}` : url
}

const HIDDEN_PATH_PREFIXES = [
  "/api/v1/admin",
  "/api/v1/utils",
  "/api/v1/api-tokens",
]

function parseSpec(spec: OApiSpec): {
  byTag: Record<string, EndpointDef[]>
  tagOrder: string[]
} {
  const byTag: Record<string, EndpointDef[]> = {}
  const tagOrder: string[] = spec.tags?.map((t) => t.name) ?? []

  for (const [path, methods] of Object.entries(spec.paths ?? {})) {
    if (HIDDEN_PATH_PREFIXES.some((p) => path.startsWith(p))) continue
    for (const [rawMethod, op] of Object.entries(methods)) {
      const method = rawMethod.toUpperCase()
      if (!["GET", "POST", "PUT", "PATCH", "DELETE"].includes(method)) continue
      const tag = op.tags?.[0] ?? "Other"
      if (!byTag[tag]) {
        byTag[tag] = []
        if (!tagOrder.includes(tag)) tagOrder.push(tag)
      }
      const pathParams = (op.parameters ?? []).filter((p) => p.in === "path")
      const queryParams = (op.parameters ?? []).filter((p) => p.in === "query")
      byTag[tag].push({
        method,
        path,
        fullPath: path,
        summary: op.summary ?? op.operationId ?? path,
        description: op.description,
        tag,
        pathParams,
        queryParams,
        hasBody: !!op.requestBody,
        bodyHint: op.requestBody?.description,
      })
    }
  }
  return { byTag, tagOrder: tagOrder.filter((t) => byTag[t]) }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function MethodBadge({ method }: { method: string }) {
  return (
    <Box
      as="span"
      px={1.5}
      py={0.5}
      borderRadius="4px"
      fontSize="10px"
      fontWeight="700"
      fontFamily="mono"
      color={METHOD_COLOR[method] ?? "#999"}
      bg={METHOD_BG[method] ?? "transparent"}
      flexShrink={0}
      minW="48px"
      textAlign="center"
    >
      {method}
    </Box>
  )
}

function StatusBadge({ status }: { status: number }) {
  const color = status < 300 ? "#38A169" : status < 400 ? "#DD6B20" : "#E53E3E"
  return (
    <Box
      as="span"
      px={2}
      py={0.5}
      borderRadius="6px"
      fontSize="13px"
      fontWeight="700"
      color={color}
      bg={`${color}18`}
    >
      {status}
    </Box>
  )
}

// Simple JSON syntax colorizer using CSS classes
function JsonDisplay({ text }: { text: string }) {
  const formatted = formatJson(text)
  // Simple tokenizer — wrap strings, numbers, booleans, null, keys with spans
  const html = formatted
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      (match) => {
        if (/^"/.test(match)) {
          if (/:$/.test(match))
            return `<span style="color:#81E6D9">${match}</span>` // key
          return `<span style="color:#F6AD55">${match}</span>` // string value
        }
        if (/true|false/.test(match))
          return `<span style="color:#68D391">${match}</span>` // boolean
        if (/null/.test(match))
          return `<span style="color:#FC8181">${match}</span>` // null
        return `<span style="color:#90CDF4">${match}</span>` // number
      },
    )
  return (
    <Box
      as="pre"
      fontSize="12px"
      fontFamily="mono"
      overflowX="auto"
      overflowY="auto"
      flex="1"
      p={3}
      lineHeight="1.6"
      // The HTML here is built locally from the JSON response with explicit
      // entity-escaping above, so there's no untrusted content to injecting.
      // biome-ignore lint/security/noDangerouslySetInnerHtml: locally-escaped JSON syntax highlighter
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

function ApiExplorer() {
  const { t } = useTranslation("apiExplorer")
  const sc = useSemanticColors()
  const borderColor = useColorModeValue("#E2E8F0", "rgba(255,255,255,0.08)")
  const panelBg = useColorModeValue(
    "rgba(247,248,252,0.9)",
    "rgba(26,32,44,0.6)",
  )
  const hoverBg = useColorModeValue("#EDF2F7", "rgba(255,255,255,0.06)")
  const activeBg = useColorModeValue(
    "rgba(52,161,221,0.12)",
    "rgba(52,161,221,0.18)",
  )
  const codeAreaBg = useColorModeValue("#1A202C", "#0D1117")
  const codeTextColor = "#E2E8F0"

  // OpenAPI spec state
  const [spec, setSpec] = useState<OApiSpec | null>(null)
  const [byTag, setByTag] = useState<Record<string, EndpointDef[]>>({})
  const [tagOrder, setTagOrder] = useState<string[]>([])
  const [specError, setSpecError] = useState<string | null>(null)
  const [specLoading, setSpecLoading] = useState(true)

  // Endpoint selection
  const [selectedEndpoint, setSelectedEndpoint] = useState<EndpointDef | null>(
    null,
  )
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set())

  // Request builder
  const [pathParams, setPathParams] = useState<Record<string, string>>({})
  const [queryParams, setQueryParams] = useState<Record<string, string>>({})
  const [requestBody, setRequestBody] = useState("")
  const [manualPath, setManualPath] = useState("")
  const [manualMethod, setManualMethod] = useState("GET")

  // Response
  const [response, setResponse] = useState<ResponseState | null>(null)
  const [sending, setSending] = useState(false)

  // Authentication guide panel
  const [authGuideOpen, setAuthGuideOpen] = useState(false)
  const [tokenCopied, setTokenCopied] = useState(false)

  const responseRef = useRef<HTMLDivElement>(null)

  // Load OpenAPI spec
  useEffect(() => {
    const url = `${API_BASE}/api/v1/openapi.json`
    setSpecLoading(true)
    setSpecError(null)
    fetch(url, {
      headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {},
    })
      .then((r) => r.json())
      .then((data: OApiSpec) => {
        setSpec(data)
        const parsed = parseSpec(data)
        setByTag(parsed.byTag)
        setTagOrder(parsed.tagOrder)
        // Expand first few tags by default
        setExpandedTags(new Set(parsed.tagOrder.slice(0, 3)))
      })
      .catch((e) => setSpecError(String(e)))
      .finally(() => setSpecLoading(false))
  }, [])

  // When endpoint is selected, pre-fill params
  function selectEndpoint(ep: EndpointDef) {
    setSelectedEndpoint(ep)
    setManualPath(ep.fullPath)
    setManualMethod(ep.method)
    const pp: Record<string, string> = {}
    for (const p of ep.pathParams) pp[p.name] = ""
    setPathParams(pp)
    const qp: Record<string, string> = {}
    for (const p of ep.queryParams) {
      qp[p.name] = p.schema?.default != null ? String(p.schema.default) : ""
    }
    setQueryParams(qp)
    setRequestBody(ep.hasBody ? "{\n  \n}" : "")
    setResponse(null)
  }

  // Apply quick demo
  function applyDemo(demo: (typeof QUICK_DEMOS)[number]) {
    const pp = { ...demo.pathParams } as Record<string, string>
    const qp = { ...demo.queryParams } as Record<string, string>
    setManualPath(demo.path)
    setManualMethod(demo.method)
    setPathParams(pp)
    setQueryParams(qp)
    setRequestBody(demo.body)
    setSelectedEndpoint(null)
    setResponse(null)
  }

  // Send request
  async function sendRequest() {
    setSending(true)
    setResponse(null)
    const effectivePath = manualPath || selectedEndpoint?.fullPath || ""
    const url = buildUrl(effectivePath, pathParams, queryParams)
    const token = getToken()
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    }
    if (token) headers.Authorization = `Bearer ${token}`

    const hasBody = ["POST", "PUT", "PATCH"].includes(manualMethod)
    const start = Date.now()
    try {
      const res = await fetch(url, {
        method: manualMethod,
        headers,
        body: hasBody && requestBody.trim() ? requestBody : undefined,
      })
      const ms = Date.now() - start
      const text = await res.text()
      const respHeaders: Record<string, string> = {}
      res.headers.forEach((v, k) => {
        respHeaders[k] = v
      })
      setResponse({
        status: res.status,
        statusText: res.statusText,
        body: text,
        ms,
        headers: respHeaders,
      })
      setTimeout(
        () =>
          responseRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          }),
        100,
      )
    } catch (e) {
      const ms = Date.now() - start
      setResponse({
        status: 0,
        statusText: "Network Error",
        body: String(e),
        ms,
        headers: {},
      })
    } finally {
      setSending(false)
    }
  }

  async function copySessionToken() {
    const tok = getToken()
    if (!tok) return
    try {
      await navigator.clipboard.writeText(tok)
      setTokenCopied(true)
      setTimeout(() => setTokenCopied(false), 1800)
    } catch {
      // clipboard API can fail in some browser contexts — ignore
    }
  }

  function toggleTag(tag: string) {
    setExpandedTags((prev) => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
  }

  const effectivePath = manualPath || selectedEndpoint?.fullPath || ""
  const pathParamNames = extractPathParams(effectivePath)
  // Sync pathParams keys to actual path
  useEffect(() => {
    if (!effectivePath) return
    const names = extractPathParams(effectivePath)
    setPathParams((prev) => {
      const next: Record<string, string> = {}
      for (const n of names) next[n] = prev[n] ?? ""
      return next
    })
  }, [effectivePath])

  return (
    <Box minH="100vh" p={{ base: 3, md: 4 }}>
      {/* Header */}
      <Flex align="center" gap={3} mb={4} wrap="wrap">
        <IconBox bg="rgba(52,161,221,0.2)" color="#34a1dd" h="40px" w="40px">
          <FiZap size={20} />
        </IconBox>
        <Flex
          minW={0}
          flex={1}
          align="baseline"
          gap={2}
          wrap="wrap"
          rowGap={0}
        >
          <Heading size={{ base: "md", md: "lg" }} color={sc.heading}>
            {t("title")}
          </Heading>
          <Text fontSize="sm" color={sc.textMuted}>
            · {t("subtitle")}
          </Text>
        </Flex>
        {spec && (
          <Box>
            <Badge colorPalette="blue" fontSize="11px">
              {spec.info?.title ?? "QDat.io API"} v{spec.info?.version}
            </Badge>
          </Box>
        )}
      </Flex>

      {/* Authentication Guide — collapsible */}
      <VisionCard mb={4} p={4}>
        <Flex
          align="center"
          gap={2}
          cursor="pointer"
          onClick={() => setAuthGuideOpen((o) => !o)}
        >
          <FiLock size={14} color="#34a1dd" />
          <Text fontSize="sm" fontWeight="600" color={sc.heading}>
            {t("authGuide.heading")}
          </Text>
          <Badge
            colorPalette={getToken() ? "green" : "red"}
            fontSize="10px"
            ml={1}
          >
            {getToken()
              ? t("authGuide.sessionActive")
              : t("authGuide.noSession")}
          </Badge>
          <Box ml="auto" color={sc.textMuted}>
            {authGuideOpen ? (
              <FiChevronDown size={14} />
            ) : (
              <FiChevronRight size={14} />
            )}
          </Box>
        </Flex>

        {authGuideOpen && (
          <Box mt={4}>
            <Text fontSize="12px" color={sc.text} mb={4}>
              {t("authGuide.browserNote")}
            </Text>

            <Heading
              as="h3"
              size="sm"
              color={sc.heading}
              mb={3}
              fontSize="13px"
              fontWeight="600"
            >
              {t("authGuide.externalUse")}
            </Heading>

            {/* Option 1 — long-lived API token */}
            <Box
              p={3}
              mb={3}
              borderRadius="8px"
              bg={panelBg}
              borderLeft="3px solid #38A169"
            >
              <Text fontSize="13px" fontWeight="600" color={sc.heading}>
                {t("authGuide.option1Title")}
              </Text>
              <Text fontSize="12px" color={sc.text} mt={1}>
                <Trans
                  i18nKey="authGuide.option1Body"
                  ns="apiExplorer"
                  components={{
                    1: (
                      <Box
                        as={Link}
                        // @ts-expect-error TanStack Link with `as` prop
                        to="/api-tokens"
                        color="#34a1dd"
                        textDecoration="underline"
                      />
                    ),
                  }}
                />
              </Text>
              <Box
                mt={2}
                p={3}
                bg={codeAreaBg}
                color={codeTextColor}
                borderRadius="6px"
                fontSize="11px"
                fontFamily="mono"
                overflowX="auto"
                whiteSpace="pre"
              >
                {`curl -H "Authorization: Bearer YOUR_API_TOKEN" \\\n  ${API_BASE}/api/v1/users/me`}
              </Box>
            </Box>

            {/* Option 2 — login JWT */}
            <Box
              p={3}
              mb={3}
              borderRadius="8px"
              bg={panelBg}
              borderLeft="3px solid #DD6B20"
            >
              <Text fontSize="13px" fontWeight="600" color={sc.heading}>
                {t("authGuide.option2Title")}
              </Text>
              <Text fontSize="12px" color={sc.text} mt={1}>
                {t("authGuide.option2Body")}
              </Text>
              <Box
                mt={2}
                p={3}
                bg={codeAreaBg}
                color={codeTextColor}
                borderRadius="6px"
                fontSize="11px"
                fontFamily="mono"
                overflowX="auto"
                whiteSpace="pre"
              >
                {`curl -X POST ${API_BASE}/api/v1/login/access-token \\\n  -H "Content-Type: application/x-www-form-urlencoded" \\\n  -d "username=you@example.com&password=YOUR_PASSWORD"`}
              </Box>
              <Text fontSize="11px" color={sc.textMuted} mt={2}>
                {t("authGuide.option2Response")}
              </Text>
              <Box
                mt={1}
                p={3}
                bg={codeAreaBg}
                color={codeTextColor}
                borderRadius="6px"
                fontSize="11px"
                fontFamily="mono"
                overflowX="auto"
                whiteSpace="pre"
              >
                {`{\n  "access_token": "eyJhbGc…",\n  "token_type": "bearer"\n}`}
              </Box>
            </Box>

            {/* Using the token */}
            <Heading
              as="h3"
              size="sm"
              color={sc.heading}
              mt={4}
              mb={2}
              fontSize="13px"
              fontWeight="600"
            >
              {t("authGuide.useToken")}
            </Heading>
            <Text fontSize="12px" color={sc.text} mb={2}>
              {t("authGuide.useTokenBody")}
            </Text>
            <Box
              p={3}
              bg={codeAreaBg}
              color={codeTextColor}
              borderRadius="6px"
              fontSize="11px"
              fontFamily="mono"
              overflowX="auto"
              whiteSpace="pre"
            >
              {"Authorization: Bearer <your-token>"}
            </Box>

            {/* Copy current session token */}
            {getToken() && (
              <Flex
                align="center"
                gap={3}
                mt={4}
                p={3}
                borderRadius="8px"
                bg={panelBg}
                wrap="wrap"
              >
                <Text fontSize="12px" color={sc.text}>
                  {t("authGuide.copySession")}
                </Text>
                <Code fontSize="11px">
                  {t("authGuide.tokenPreview", {
                    prefix: getToken()?.slice(0, 12) ?? "",
                  })}
                </Code>
                <VisionButton
                  size="sm"
                  variant="ghost"
                  onClick={copySessionToken}
                  fontSize="11px"
                  h="28px"
                  px={2}
                  ml="auto"
                >
                  <FiCopy size={12} />
                  {tokenCopied
                    ? t("authGuide.copied")
                    : t("authGuide.copyButton")}
                </VisionButton>
              </Flex>
            )}
          </Box>
        )}
      </VisionCard>

      {/* Quick Demo Buttons */}
      <VisionCard mb={4} p={4}>
        <Flex align="center" gap={2} mb={3}>
          <FiZap size={14} color="#34a1dd" />
          <Text fontSize="sm" fontWeight="600" color={sc.heading}>
            {t("demos.heading")}
          </Text>
        </Flex>
        <Flex gap={2} flexWrap="wrap">
          {QUICK_DEMOS.map((demo) => (
            <VisionButton
              key={demo.labelKey}
              size="sm"
              variant={
                (demo as { highlight?: boolean }).highlight ? "brand" : "ghost"
              }
              onClick={() => applyDemo(demo)}
              fontSize="12px"
              h="32px"
              px={3}
            >
              {demo.icon} {t(demo.labelKey)}
            </VisionButton>
          ))}
        </Flex>
        <Text fontSize="11px" color={sc.textMuted} mt={2}>
          {t("demos.tokenNote")}
        </Text>
      </VisionCard>

      {/* Main layout: endpoint list + request/response */}
      <Grid
        templateColumns={{ base: "1fr", lg: "280px 1fr" }}
        gap={4}
        alignItems="start"
      >
        {/* Left panel: endpoint list */}
        <VisionCard
          p={0}
          overflow="hidden"
          position={{ lg: "sticky" }}
          top={{ lg: "20px" }}
        >
          <Box p={3} borderBottom="1px solid" borderColor={borderColor}>
            <Text fontSize="sm" fontWeight="600" color={sc.heading}>
              {t("endpoints.heading")}
            </Text>
            {specLoading && (
              <Flex align="center" gap={2} mt={2}>
                <Spinner size="xs" color="#34a1dd" />
                <Text fontSize="12px" color={sc.textMuted}>
                  {t("endpoints.loading")}
                </Text>
              </Flex>
            )}
            {specError && (
              <Text fontSize="11px" color="#E53E3E" mt={1}>
                {t("endpoints.error")}
              </Text>
            )}
          </Box>
          <Box
            maxH={{ base: "300px", lg: "calc(100vh - 320px)" }}
            overflowY="auto"
          >
            {tagOrder.map((tag) => {
              const endpoints = byTag[tag] ?? []
              const isOpen = expandedTags.has(tag)
              return (
                <Box key={tag}>
                  {/* Tag header */}
                  <Flex
                    align="center"
                    gap={2}
                    px={3}
                    py={2}
                    cursor="pointer"
                    onClick={() => toggleTag(tag)}
                    bg={panelBg}
                    borderBottom="1px solid"
                    borderColor={borderColor}
                    _hover={{ bg: hoverBg }}
                  >
                    {isOpen ? (
                      <FiChevronDown size={12} />
                    ) : (
                      <FiChevronRight size={12} />
                    )}
                    <Text
                      fontSize="12px"
                      fontWeight="600"
                      color={sc.heading}
                      flex="1"
                      textTransform="capitalize"
                    >
                      {tag}
                    </Text>
                    <Text fontSize="10px" color={sc.textMuted}>
                      {endpoints.length}
                    </Text>
                  </Flex>
                  {/* Endpoints */}
                  {isOpen &&
                    endpoints.map((ep) => {
                      const isSelected =
                        selectedEndpoint?.method === ep.method &&
                        selectedEndpoint?.path === ep.path
                      return (
                        <Flex
                          key={`${ep.method}:${ep.path}`}
                          align="flex-start"
                          gap={2}
                          px={3}
                          py={2}
                          cursor="pointer"
                          bg={isSelected ? activeBg : "transparent"}
                          borderBottom="1px solid"
                          borderColor={borderColor}
                          _hover={{ bg: isSelected ? activeBg : hoverBg }}
                          onClick={() => selectEndpoint(ep)}
                        >
                          <Box pt="2px">
                            <MethodBadge method={ep.method} />
                          </Box>
                          <Text
                            fontSize="11px"
                            color={isSelected ? "#34a1dd" : sc.text}
                            fontFamily="mono"
                            lineHeight="1.4"
                            wordBreak="break-all"
                          >
                            {ep.path}
                          </Text>
                        </Flex>
                      )
                    })}
                </Box>
              )
            })}
          </Box>
        </VisionCard>

        {/* Right panel: request builder + response */}
        <Stack gap={4}>
          {/* Request builder */}
          <VisionCard p={4}>
            <Text fontSize="sm" fontWeight="600" color={sc.heading} mb={3}>
              {t("request.heading")}
            </Text>

            {/* Method + URL bar */}
            <Flex gap={2} mb={4} align="center">
              <select
                value={manualMethod}
                onChange={(e) => setManualMethod(e.target.value)}
                style={{
                  background: METHOD_BG[manualMethod] ?? "transparent",
                  color: METHOD_COLOR[manualMethod] ?? "inherit",
                  border: `1px solid ${borderColor}`,
                  borderRadius: "8px",
                  padding: "8px 12px",
                  fontSize: "13px",
                  fontWeight: "700",
                  fontFamily: "monospace",
                  cursor: "pointer",
                  outline: "none",
                  minWidth: "90px",
                }}
              >
                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                  <option
                    key={m}
                    value={m}
                    style={{ background: "#1A202C", color: METHOD_COLOR[m] }}
                  >
                    {m}
                  </option>
                ))}
              </select>
              <Input
                value={manualPath}
                onChange={(e) => setManualPath(e.target.value)}
                placeholder={t("request.pathPlaceholder")}
                fontFamily="mono"
                fontSize="13px"
                flex="1"
                borderColor={borderColor}
              />
              <VisionButton
                onClick={sendRequest}
                loading={sending}
                minW="100px"
              >
                <FiPlay size={14} />
                {t("request.send")}
              </VisionButton>
            </Flex>

            {/* Base URL display */}
            <Text fontSize="11px" color={sc.textMuted} mb={4} fontFamily="mono">
              {t("request.baseLabel")} <Code fontSize="11px">{API_BASE}</Code>
            </Text>

            {/* Endpoint summary */}
            {selectedEndpoint?.summary && (
              <Box
                mb={4}
                p={3}
                borderRadius="8px"
                bg={panelBg}
                borderLeft="3px solid #34a1dd"
              >
                <Text fontSize="12px" fontWeight="600" color={sc.heading}>
                  {selectedEndpoint.summary}
                </Text>
                {selectedEndpoint.description && (
                  <Text fontSize="11px" color={sc.textMuted} mt={1}>
                    {selectedEndpoint.description}
                  </Text>
                )}
              </Box>
            )}

            <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
              {/* Path Parameters */}
              {pathParamNames.length > 0 && (
                <Box>
                  <Text
                    fontSize="12px"
                    fontWeight="600"
                    color={sc.heading}
                    mb={2}
                  >
                    {t("request.pathParameters")}
                  </Text>
                  <Stack gap={2}>
                    {pathParamNames.map((name) => {
                      const spec = selectedEndpoint?.pathParams.find(
                        (p) => p.name === name,
                      )
                      return (
                        <Box key={name}>
                          <Text
                            fontSize="11px"
                            color={sc.textMuted}
                            mb={1}
                            fontFamily="mono"
                          >
                            {name}
                            {spec?.required !== false && (
                              <Text as="span" color="#E53E3E">
                                {" "}
                                *
                              </Text>
                            )}
                            {spec?.description && (
                              <Text
                                as="span"
                                color={sc.textMuted}
                                fontFamily="sans-serif"
                              >
                                {" "}
                                — {spec.description}
                              </Text>
                            )}
                          </Text>
                          <Input
                            value={pathParams[name] ?? ""}
                            onChange={(e) =>
                              setPathParams((p) => ({
                                ...p,
                                [name]: e.target.value,
                              }))
                            }
                            placeholder={t("request.enterValue", { name })}
                            fontFamily="mono"
                            fontSize="12px"
                            size="sm"
                            borderColor={borderColor}
                          />
                        </Box>
                      )
                    })}
                  </Stack>
                </Box>
              )}

              {/* Query Parameters */}
              {Object.keys(queryParams).length > 0 ||
              selectedEndpoint?.queryParams.length ? (
                <Box>
                  <Flex align="center" justify="space-between" mb={2}>
                    <Text fontSize="12px" fontWeight="600" color={sc.heading}>
                      {t("request.queryParameters")}
                    </Text>
                    {Object.keys(queryParams).length === 0 && (
                      <Text fontSize="11px" color={sc.textMuted}>
                        {t("request.queryEmptyHint")}
                      </Text>
                    )}
                  </Flex>
                  <Stack gap={2}>
                    {(selectedEndpoint?.queryParams ?? []).map((p) => (
                      <Box key={p.name}>
                        <Text
                          fontSize="11px"
                          color={sc.textMuted}
                          mb={1}
                          fontFamily="mono"
                        >
                          {p.name}
                          {p.required && (
                            <Text as="span" color="#E53E3E">
                              {" "}
                              *
                            </Text>
                          )}
                          {p.schema?.type && (
                            <Text as="span" color="#805AD5">
                              {" "}
                              ({p.schema.type})
                            </Text>
                          )}
                          {p.description && (
                            <Text
                              as="span"
                              color={sc.textMuted}
                              fontFamily="sans-serif"
                            >
                              {" "}
                              — {p.description}
                            </Text>
                          )}
                        </Text>
                        {p.schema?.enum ? (
                          <select
                            value={queryParams[p.name] ?? ""}
                            onChange={(e) =>
                              setQueryParams((q) => ({
                                ...q,
                                [p.name]: e.target.value,
                              }))
                            }
                            style={{
                              fontSize: "12px",
                              fontFamily: "monospace",
                              width: "100%",
                              border: `1px solid ${borderColor}`,
                              borderRadius: "8px",
                              padding: "6px 8px",
                              background: "transparent",
                              color: "inherit",
                            }}
                          >
                            <option value="">
                              {t("request.queryOptional")}
                            </option>
                            {p.schema.enum.map((v) => (
                              <option key={v} value={v}>
                                {v}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <Input
                            value={queryParams[p.name] ?? ""}
                            onChange={(e) =>
                              setQueryParams((q) => ({
                                ...q,
                                [p.name]: e.target.value,
                              }))
                            }
                            placeholder={
                              p.schema?.default != null
                                ? String(p.schema.default)
                                : `${p.name}…`
                            }
                            fontFamily="mono"
                            fontSize="12px"
                            size="sm"
                            borderColor={borderColor}
                          />
                        )}
                      </Box>
                    ))}
                    {/* Extra params not from spec */}
                    {Object.keys(queryParams)
                      .filter(
                        (k) =>
                          !selectedEndpoint?.queryParams.find(
                            (p) => p.name === k,
                          ),
                      )
                      .map((k) => (
                        <Flex key={k} gap={2} align="center">
                          <Code fontSize="11px">{k}</Code>
                          <Input
                            value={queryParams[k]}
                            onChange={(e) =>
                              setQueryParams((q) => ({
                                ...q,
                                [k]: e.target.value,
                              }))
                            }
                            fontFamily="mono"
                            fontSize="12px"
                            size="sm"
                            borderColor={borderColor}
                          />
                        </Flex>
                      ))}
                  </Stack>
                </Box>
              ) : null}
            </Grid>

            {/* Request Body */}
            {["POST", "PUT", "PATCH"].includes(manualMethod) && (
              <Box mt={4}>
                <Flex align="center" justify="space-between" mb={2}>
                  <Text fontSize="12px" fontWeight="600" color={sc.heading}>
                    {t("request.body")}{" "}
                    <Text as="span" color={sc.textMuted} fontWeight="400">
                      {t("request.bodyJson")}
                    </Text>
                  </Text>
                  <VisionButton
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      try {
                        setRequestBody(
                          JSON.stringify(JSON.parse(requestBody), null, 2),
                        )
                      } catch {
                        // ignore
                      }
                    }}
                    fontSize="11px"
                    h="24px"
                    px={2}
                  >
                    {t("request.format")}
                  </VisionButton>
                </Flex>
                <Textarea
                  value={requestBody}
                  onChange={(e) => setRequestBody(e.target.value)}
                  placeholder={t("request.bodyPlaceholder")}
                  fontFamily="mono"
                  fontSize="12px"
                  rows={8}
                  borderColor={borderColor}
                  resize="vertical"
                />
              </Box>
            )}

            {/* Auth status */}
            <Flex align="center" gap={2} mt={3}>
              <Box
                w="8px"
                h="8px"
                borderRadius="full"
                bg={getToken() ? "#38A169" : "#E53E3E"}
                flexShrink={0}
              />
              <Text fontSize="11px" color={sc.textMuted}>
                {getToken()
                  ? t("auth.authenticated")
                  : t("auth.notAuthenticated")}
              </Text>
            </Flex>
          </VisionCard>

          {/* Response panel */}
          <Box ref={responseRef}>
            <VisionCard p={0} overflow="hidden">
              <Flex
                align="center"
                gap={3}
                p={3}
                borderBottom="1px solid"
                borderColor={borderColor}
              >
                <Text fontSize="sm" fontWeight="600" color={sc.heading}>
                  {t("response.heading")}
                </Text>
                {response && (
                  <>
                    <StatusBadge status={response.status} />
                    <Text fontSize="12px" color={sc.textMuted}>
                      {response.statusText}
                    </Text>
                    <Text fontSize="12px" color={sc.textMuted} ml="auto">
                      {response.ms} ms
                    </Text>
                    {response.headers["content-type"] && (
                      <Code fontSize="11px" color={sc.textMuted}>
                        {response.headers["content-type"].split(";")[0]}
                      </Code>
                    )}
                  </>
                )}
                {sending && (
                  <Flex align="center" gap={2}>
                    <Spinner size="xs" color="#34a1dd" />
                    <Text fontSize="12px" color={sc.textMuted}>
                      {t("request.sending")}
                    </Text>
                  </Flex>
                )}
                {!response && !sending && (
                  <Text fontSize="12px" color={sc.textMuted}>
                    {t("response.hint")}
                  </Text>
                )}
              </Flex>

              {response && (
                <Box
                  bg={codeAreaBg}
                  color={codeTextColor}
                  minH="200px"
                  maxH="600px"
                  overflow="auto"
                >
                  <JsonDisplay text={response.body} />
                </Box>
              )}

              {!response && !sending && (
                <Box p={8} textAlign="center">
                  <Text fontSize="13px" color={sc.textMuted}>
                    {t("response.emptyTitle")}{" "}
                    <Text as="span" color="#34a1dd">
                      {t("response.emptyTitleSend")}
                    </Text>{" "}
                    {t("response.emptyTitleEnd")}
                  </Text>
                  <Text fontSize="12px" color={sc.textMuted} mt={2}>
                    {t("response.emptyDemo")}{" "}
                    <Text as="span" color="#34a1dd">
                      {t("response.emptyDemoTarget")}
                    </Text>{" "}
                    {t("response.emptyDemoEnd")}
                  </Text>
                </Box>
              )}
            </VisionCard>
          </Box>

          {/* Usage tip: Temperature History */}
          <VisionCard p={4} borderLeft="3px solid #34a1dd">
            <Text fontSize="13px" fontWeight="600" color={sc.heading} mb={2}>
              {t("usageTip.title")}
            </Text>
            <Stack gap={1}>
              <Text fontSize="12px" color={sc.text}>
                1.{" "}
                <Trans
                  i18nKey="usageTip.step1"
                  ns="apiExplorer"
                  components={{ 1: <strong /> }}
                />
              </Text>
              <Text fontSize="12px" color={sc.text}>
                2. {t("usageTip.step2Pre")}{" "}
                <Code fontSize="11px">{t("usageTip.step2Path")}</Code>{" "}
                {t("usageTip.step2PathLabel")} {t("usageTip.step2Find")}{" "}
                <Code fontSize="11px">GET /api/v1/tags/</Code>{" "}
                {t("usageTip.step2FindEnd")}
              </Text>
              <Text fontSize="12px" color={sc.text}>
                3. {t("usageTip.step3Pre")}{" "}
                <Code fontSize="11px">start_time</Code> {t("usageTip.step3And")}{" "}
                <Code fontSize="11px">end_time</Code> {t("usageTip.step3Suf")}
              </Text>
              <Text fontSize="12px" color={sc.text}>
                4.{" "}
                <Trans
                  i18nKey="usageTip.step4Pre"
                  ns="apiExplorer"
                  components={{ 1: <strong /> }}
                />{" "}
                <Code fontSize="11px">{t("usageTip.step4Timestamp")}</Code>
                {t("usageTip.step4Comma")}{" "}
                <Code fontSize="11px">{t("usageTip.step4Value")}</Code>{" "}
                {t("usageTip.step4End")}
              </Text>
            </Stack>
            <Box
              mt={3}
              p={3}
              bg={codeAreaBg}
              borderRadius="8px"
              color={codeTextColor}
              fontSize="11px"
              fontFamily="mono"
            >
              <Text color="#81E6D9">GET</Text>{" "}
              <Text as="span" color="#F6AD55">
                {API_BASE}/api/v1/tags/{"{"}
                <Text as="span" color="#FC8181">
                  uuid
                </Text>
                {"}"}/readings
              </Text>
              {"?start_time=2026-01-01T00:00:00Z&limit=1000"}
              <br />
              <Text color="#68D391">
                Authorization: Bearer &lt;your-token&gt;
              </Text>
            </Box>
          </VisionCard>
        </Stack>
      </Grid>
    </Box>
  )
}
